/**
 * Контрольный тест: правила до старта, метка теста во время (ТЗ UI-51).
 *
 * Раньше нажатие «Пройти контрольный тест» сразу открывало набор, и правила
 * человек узнавал по ходу: что Backspace не работает, выяснялось первой же
 * опечаткой. Условия, которые нельзя отменить, читают до начала, а не после.
 *
 * Сам набор не меняется ничем: тот же экран, те же токены состояний, тот же
 * запрет движения и те же особые правила теста — отсутствие подсказок и
 * запрет исправлений приходят из `hintLevel`, как и раньше.
 */

import { useState } from 'react';
import type { LayoutId, SessionId } from '@type2x/shared-contracts';
import type { Translate } from '@type2x/i18n';
import type { TypingSession } from '@type2x/typing-engine';
import type { IntegritySignals } from '@type2x/adapter-web';
import { Button, Card } from '@type2x/ui';

import type { Runtime } from '../runtime.js';
import { TypingScreen } from './TypingScreen.js';

/** Правила теста: то, что нельзя узнать по ходу и уже не отменить. */
const RULES = [
  'control.rule.unfamiliar',
  'control.rule.noCorrections',
  'control.rule.noHints',
  'control.rule.progressKept',
] as const;

export interface ControlTestScreenProps {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly sessionId: SessionId;
  readonly layoutId: LayoutId;
  readonly targetText: string;
  /** Отложенная проверка отличается от обычного теста только названием. */
  readonly retention: boolean;
  readonly onFinished: (session: TypingSession, signals: IntegritySignals) => void;
}

export function ControlTestScreen({
  runtime,
  t,
  sessionId,
  layoutId,
  targetText,
  retention,
  onFinished,
}: ControlTestScreenProps): React.JSX.Element {
  const [started, setStarted] = useState(false);
  const title = t(retention ? 'control.retentionTitle' : 'control.title');

  if (!started) {
    return (
      <Card title={title}>
        <p className="t2x-muted">{t(retention ? 'control.retentionHint' : 'control.hint')}</p>

        <ul className="t2x-rules" aria-label={t('control.rules')}>
          {RULES.map((rule) => (
            <li key={rule}>{t(rule)}</li>
          ))}
        </ul>

        <Button onClick={() => setStarted(true)}>{t('control.begin')}</Button>
      </Card>
    );
  }

  return (
    <div className="t2x-control">
      {/*
        Метка теста стоит рядом с текстом всё время набора (ТЗ UI-51): экран
        занятия и экран теста устроены одинаково, и без метки человек не
        отличит проверку от тренировки.
      */}
      <p className="t2x-badge t2x-badge--control">{t('control.badge')}</p>

      <TypingScreen
        key={sessionId}
        runtime={runtime}
        t={t}
        title={title}
        hint={t(retention ? 'control.retentionHint' : 'control.hint')}
        sessionId={sessionId}
        layoutId={layoutId}
        targetText={targetText}
        hintLevel="no-hints"
        trackIntegrity
        onFinished={onFinished}
      />
    </div>
  );
}
